import React, { useState } from 'react';
import '../styles/practice-areas.css';

interface PracticeAreasProps {
  title?: string;
  subtitle?: string;
  description?: string;
  sectionNumber?: string;
}

const PracticeAreas: React.FC<PracticeAreasProps> = ({
  title = "Practice Areas",
  subtitle = "Comprehensive Personal Injury Representation",
  description = "Expert legal advocacy for accident victims across California. We fight to get you the compensation you deserve.",
  sectionNumber = "0003"
}) => {
  // Default practice areas data
  const practiceAreas = [
    {
      id: "personal-injury",
      title: "Personal Injury",
      description: "Expert representation for individuals injured due to negligence or wrongdoing, ensuring you receive fair compensation.",
      image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&h=400&fit=crop"
    },
    {
      id: "car-accidents", 
      title: "Car Accidents",
      description: "Comprehensive legal support for vehicle collision victims, handling insurance claims and securing maximum compensation.",
      image: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=600&h=400&fit=crop"
    },
    {
      id: "lyft-uber-accidents",
      title: "Lyft & Uber Accidents", 
      description: "Specialized representation for rideshare accident cases, navigating complex insurance policies and liability issues.",
      image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=600&h=400&fit=crop"
    },
    {
      id: "truck-accidents",
      title: "Truck Accidents",
      description: "Aggressive advocacy for victims of commercial truck accidents, dealing with corporate defendants and complex regulations.",
      image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=600&h=400&fit=crop"
    },
    {
      id: "motorcycle-accidents",
      title: "Motorcycle Accidents",
      description: "Dedicated support for motorcyclists injured in accidents, fighting bias and securing fair compensation for your injuries.",
      image: "https://images.unsplash.com/photo-1558981852-426c6c22a060?w=600&h=400&fit=crop"
    },
    {
      id: "pedestrian-accidents",
      title: "Pedestrian Accidents",
      description: "Protecting the rights of pedestrians struck by vehicles, ensuring accountability and full compensation for injuries.",
      image: "https://images.unsplash.com/photo-1485081669829-bacb8c7bb1f3?w=600&h=400&fit=crop"
    },
    {
      id: "wrongful-death",
      title: "Wrongful Death",
      description: "Compassionate legal guidance for families who have lost loved ones due to negligence or wrongful acts.",
      image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=600&h=400&fit=crop"
    },
    {
      id: "slip-trip-falls",
      title: "Slip/Trip and Falls",
      description: "Holding property owners accountable for unsafe conditions that cause injuries on their premises.",
      image: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=600&h=400&fit=crop"
    },
    {
      id: "premises-liability",
      title: "Premises Liability",
      description: "Expert representation for injuries occurring on another's property due to negligent maintenance or hazardous conditions.",
      image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop"
    },
    {
      id: "dog-bites",
      title: "Dog Bites",
      description: "Protecting victims of animal attacks, pursuing compensation for medical expenses, pain, and suffering.",
      image: "https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&h=400&fit=crop"
    },
    {
      id: "catastrophic-injuries",
      title: "Catastrophic Injuries",
      description: "Comprehensive legal support for life-altering injuries, ensuring long-term care and financial security.",
      image: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=600&h=400&fit=crop"
    },
    {
      id: "uninsured-motorist",
      title: "Uninsured-Motorist",
      description: "Protecting your rights when injured by drivers without insurance, maximizing your own policy benefits.",
      image: "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=600&h=400&fit=crop"
    }
  ];
  const [expandedArea, setExpandedArea] = useState<string | null>(null);

  const handleMouseEnter = (areaId: string) => {
    setExpandedArea(areaId);
  };

  const handleMouseLeave = () => {
    setExpandedArea(null);
  };

  return (
    <div className="practice-areas-container">
      {/* Header Section */}
      <div className="practice-areas-header">
        <div className="practice-areas-title-section">
          <h2 className="practice-areas-title">{title}</h2>
        </div>
        <div className="practice-areas-subtitle-section">
          <h1 className="practice-areas-subtitle">{subtitle}</h1>
          <p className="practice-areas-description">{description}</p>
        </div>
      </div>

      {/* Practice Areas List */}
      <div className="practice-areas-list">
        {practiceAreas.map((area, index) => (
          <div
            key={area.id}
            className={`practice-area-item ${
              expandedArea === area.id ? 'expanded' : ''
            }`}
            onMouseEnter={() => handleMouseEnter(area.id)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Background Image */}
            <div 
              className="practice-area-image"
              style={{ backgroundImage: `url(${area.image})` }}
            >
              <div className="practice-area-overlay"></div>
            </div>
            
            <div className="practice-area-content">
              <h3 className="practice-area-title">{area.title}</h3>
              <div className="practice-area-arrow">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 14L12 9L17 14"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
            
            {/* Expanded Description */}
            <div className="practice-area-description">
              <p>{area.description}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PracticeAreas;

